import { Link } from 'react-router-dom'
import { FilePen, FileCheck, FolderLock, QrCode, Send, PenLine, FileSignature } from 'lucide-react'
import { useAuth } from '@/shared/contexts/AuthContext'

interface ActionCard {
  to: string
  title: string
  description: string
  icon: React.ReactNode
  accent: string
  primary?: boolean
}

const cards: ActionCard[] = [
  {
    to: '/firmar',
    title: 'Firmar Documento',
    description: 'Firma electrónica o digital de PDFs con posicionamiento visual y QR de validación.',
    icon: <FilePen size={28} />,
    accent: 'from-primary-600 to-blue-600',
    primary: true,
  },
  {
    to: '/documentos',
    title: 'Documentos',
    description: 'Enviá un PDF para que otra persona lo firme. Tracking de estado por destinatario (pending → viewed → signed).',
    icon: <Send size={28} />,
    accent: 'from-emerald-500 to-teal-600',
  },
  {
    to: '/verifier',
    title: 'Verificar Firmas',
    description: 'Pericia técnica de firmas digitales en PDFs (Ley 25.506).',
    icon: <FileCheck size={28} />,
    accent: 'from-amber-500 to-orange-600',
  },
  {
    to: '/custody',
    title: 'Custodia Documental',
    description: 'Registra el hash de un documento con constancia legal de fecha cierta.',
    icon: <FolderLock size={28} />,
    accent: 'from-purple-500 to-fuchsia-600',
  },
  {
    to: '/mis-firmas',
    title: 'Mis Firmas',
    description: 'Documentos firmados desde tu cuenta.',
    icon: <PenLine size={28} />,
    accent: 'from-sky-500 to-cyan-600',
  },
  {
    to: '/qr-generator',
    title: 'Generador QR',
    description: 'Codigos QR para traslado de demanda, carta documento u otros procesos.',
    icon: <QrCode size={28} />,
    accent: 'from-rose-500 to-pink-600',
  },
  // Blockchain Lookup oculto del dashboard. Ruta /blockchain-lookup
  // sigue activa para enlaces directos. Re-incluir cuando se posicione
  // como caso de uso legal (evidencia cripto en juicios).
]

export function Dashboard() {
  const { user } = useAuth()
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches'
  const name = user?.user_metadata?.full_name || user?.email?.split('@')[0] || ''

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center">
            <FileSignature size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-legal-900 dark:text-white">
              {greeting}{name ? `, ${name}` : ''}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm">¿Qué querés hacer hoy?</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map(card => (
          <Link
            key={card.to}
            to={card.to}
            className={`group relative overflow-hidden rounded-xl border p-6 transition-all hover:shadow-xl hover:-translate-y-0.5 ${
              card.primary
                ? 'border-primary-200 dark:border-primary-800 bg-gradient-to-br from-white to-primary-50 dark:from-legal-800 dark:to-legal-700 sm:col-span-2'
                : 'border-legal-200 dark:border-legal-700 bg-white dark:bg-legal-800'
            }`}
          >
            <div className={`absolute top-0 right-0 w-32 h-32 -mr-10 -mt-10 rounded-full bg-gradient-to-br ${card.accent} opacity-10 group-hover:opacity-20 transition-opacity`} />
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br ${card.accent} text-white mb-4 shadow-lg`}>
              {card.icon}
            </div>
            <h2 className="text-lg font-bold text-legal-900 dark:text-white mb-1">{card.title}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{card.description}</p>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
          <h3 className="font-bold text-blue-900 dark:text-blue-200 mb-2">¿Cuándo usar SignWiz?</h3>
          <p className="text-sm text-blue-900 dark:text-blue-200 mb-2">
            Para el <strong>80% de los casos cotidianos</strong>: contratos privados, boletos, autorizaciones,
            constancias, formularios. Cero fricción, validez probatoria art. 5 Ley 25.506.
          </p>
          <p className="text-xs text-blue-800 dark:text-blue-300">
            Toda firma criptografía PAdES (verificable en Adobe Reader) + QR de verificación pública + URL corta + hash SHA-256.
          </p>
        </div>
        <div className="p-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
          <h3 className="font-bold text-amber-900 dark:text-amber-200 mb-2">¿Cuándo usar firma digital con CA?</h3>
          <p className="text-sm text-amber-900 dark:text-amber-200 mb-2">
            Para casos críticos con <strong>presunción legal plena</strong> (art. 9 Ley 25.506):
            actos administrativos, escrituras digitales, expedientes judiciales.
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Subí tu certificado .p12/.pfx de una CA (AFIP, ONTI, etc.) en el flujo de Firmar Documento.
          </p>
        </div>
      </div>
    </div>
  )
}
